import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { environment } from '@lohi/environments/environment';
import * as firebase from 'firebase';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, pluck, shareReplay, take } from 'rxjs/operators';
import { SimpleFinn } from '../utilities/simpleFinn';

export interface LoHiUserInfo {
  id: string;
  allowDriverDirections: boolean;
  cdl: string;
  cdlStateAbbreviation: string;
  cdlStateId: string;
  cdlStateName: string;
  companyCanInviteDrivers: boolean;
  driverName: string;
  phoneNumber: string;
  verified: boolean;
  verifiedTimestamp: string;
  companyName: string;
  companyId: string;
  companyOwnerName: string;
  companyOwnerPhone: string;
  permissions: string[];
  homebaseLng: number;
  homebaseLat: number;
  allowDriverBooking: boolean;
  allowDriverCreateTruck: boolean;
  allowDriverCreateTrailer: boolean;
  companyDriversCanViewTourDollarAmounts: boolean;
  companyDriversCanViewTourRateCon: boolean;
  companyDriversCanApproveTours: boolean;
  accountRelationship: 'saas' | 'broker' | 'carrier';
  hasDispatcher: boolean;
  hasELDIntegration: boolean;
  showEarningsPrediction: true;
  showOrderPricing: true;
  earningsProgramMember: boolean;
  isMarketplaceEnabled: boolean;
  isYMTEnabled: boolean;
  isLoHiPayMerchant: boolean;
  isLoHiPayConsumer: boolean;
  lohipayDriversAutoApprovePayment: boolean;
  createAssetWithMarketplace: boolean;
  isFiveFTourDispatcher: boolean;
  ffDriverCut: number; // 35% is 0.35
  isCompanyFiveFTourOnly: boolean;
  enableTours: boolean;
  canViewTourDollarAmounts: boolean;
  canApproveTours: boolean;
  isCompanySuspendedFromWorking?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userInfo$$ = new BehaviorSubject<LoHiUserInfo>(null);
  public userInfo$ = this.userInfo$$.asObservable().pipe(shareReplay(1));
  private httpClientNoInterceptors: HttpClient;
  private isLoggedIn$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private customToken: SimpleFinn<string>;

  public get customToken$() {
    return this.customToken.get$();
  }

  public isLoggedIn$ = this.isLoggedIn$$.asObservable().pipe(
    filter((isLoggedIn) => isLoggedIn !== null),
    distinctUntilChanged(),
    shareReplay(1),
  );

  constructor(
    httpBackend: HttpBackend,
    private logger: NGXLogger,
    private afAuth: AngularFireAuth,
    private httpClient: HttpClient,
  ) {
    this.httpClientNoInterceptors = new HttpClient(httpBackend);
    this.customToken = new SimpleFinn<string>(null, this.loadCustomToken);

    this.afAuth.authState.subscribe(async (user) => {
      if (user) {
        await this.loadUserInfo();
        const userInfo = await this.userInfo$.pipe(take(1)).toPromise();
        if (!userInfo) {
          this.isLoggedIn$$.next(false);
          this.logout();
          return;
        }
        this.isLoggedIn$$.next(true);
      } else {
        this.isLoggedIn$$.next(false);
      }
    });
  }

  public async loginWithPhone(phoneNumber: string, password: string): Promise<boolean> {
    try {
      const result = await this.httpClientNoInterceptors
        .post<{ customToken: string }>(
          `${environment.api}/v1/login/password`,
          { phoneNumber, password },
          {
            headers: {
              'Content-Type': 'application/json',
              'web-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
              'web-timezone-offset-minutes': `${new Date().getTimezoneOffset()}`,
            },
          },
        )
        .toPromise();
      await this.afAuth.signInWithCustomToken(result.customToken);
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async loginWithTextMessage(
    phoneNumber: string,
    verifier: firebase.auth.ApplicationVerifier,
  ): Promise<firebase.auth.ConfirmationResult> {
    return this.afAuth.signInWithPhoneNumber(phoneNumber, verifier);
  }

  public async loginWithLoHiToken(token: string): Promise<boolean> {
    try {
      const result = await this.httpClientNoInterceptors
        .get<{ customToken: string }>(`${environment.api}/v1/login/lohi_token`, {
          headers: {
            'Content-Type': 'application/json',
            'web-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
            'web-timezone-offset-minutes': `${new Date().getTimezoneOffset()}`,
          },
          params: {
            token,
          },
        })
        .toPromise();
      await this.afAuth.signInWithCustomToken(result.customToken);
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async loginWithCustomToken(token: string): Promise<boolean> {
    try {
      await this.afAuth.signInWithCustomToken(token);
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  private loadCustomToken = (): Promise<string> => {
    try {
      return this.httpClient
        .get<{ customToken: string }>(`${environment.api}/v1/auth_token`)
        .pipe(pluck('customToken'))
        .toPromise();
    } catch (e) {
      return null;
    }
  }
  public async getPasswordResetCode(phoneNumber: string): Promise<string> {
    try {
      const result = await this.httpClientNoInterceptors
        .post<{ message: string }>(
          `${environment.api}/v1/password_reset_code`,
          {
            phoneNumber,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .toPromise();
      return result.message;
    } catch (error) {
      this.logger.error(error);
      return null;
    }
  }

  public async doPasswordReset(resetCode: string, newPassword: string): Promise<string> {
    try {
      const result = await this.httpClientNoInterceptors
        .post<{ message: string }>(
          `${environment.api}/v1/do_password_reset`,
          {
            resetCode,
            newPassword,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .toPromise();
      return result.message;
    } catch (error) {
      this.logger.error(error);
      return null;
    }
  }

  public async logout(redirect = true) {
    await this.afAuth.signOut();
    if (redirect) {
      window.location.pathname = '/';
    }
  }

  public async loadUserInfo() {
    const token = await this.afAuth.idToken
      .pipe(
        filter((t) => !!t),
        take(1),
      )
      .toPromise();
    const userInfo = await this.httpClientNoInterceptors
      .get<LoHiUserInfo>(`${environment.api}/v1/user_info`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'web-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          'web-timezone-offset-minutes': `${new Date().getTimezoneOffset()}`,
        },
      })
      .toPromise();
    this.userInfo$$.next(userInfo);
  }
}

export function isAdmin(userInfo: LoHiUserInfo): boolean {
  return !!userInfo?.permissions?.includes('admin');
}

export function isDriver(userInfo: LoHiUserInfo): boolean {
  return !!userInfo?.permissions?.includes('driver');
}

export function isFiveFTourDispatcher(userInfo: LoHiUserInfo): boolean {
  return !!userInfo?.permissions?.includes('5f-tour-dispatcher');
}

export function isNotOnlyFiveFTourOwner(userInfo: LoHiUserInfo): boolean {
  return !userInfo.isCompanyFiveFTourOnly;
}
